
import { useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Plus, Minus, Trash2, Loader2, Star, Tag } from 'lucide-react';
import { deliveryApi, getUnitPriceForQuantity } from '../api/delivery';
import { useCartStore } from '../store/cart';
import { getBranding, fmt } from '../lib/branding';
import { getUpsellProducts } from '../lib/upsell';
import { ProductCard } from '../components/ProductCard';

// Sub-página de produto (12/07/2026, a pedido do Thales): clicar num
// produto da vitrine abre essa rota dedicada (/:slug/produto/:productId)
// em vez de só adicionar direto ao carrinho. Reaproveita as mesmas query
// keys ['store', slug] e ['catalog', slug] de StorePage.tsx -- se o
// cliente já visitou a vitrine, os dados vêm do cache do React Query na
// hora (sem loading); se caiu direto aqui (ex: link compartilhado), busca
// tudo do zero normalmente.
export default function ProductPage() {
  const { slug, productId } = useParams<{ slug: string; productId: string }>();
  const navigate = useNavigate();
  const { addItem, items, updateQuantity, removeItem } = useCartStore();

  const { data: store } = useQuery({
    queryKey: ['store', slug],
    queryFn: () => deliveryApi.getStoreInfo(slug!),
    enabled: !!slug,
  });

  const { data: catalog, isLoading } = useQuery({
    queryKey: ['catalog', slug],
    queryFn: () => deliveryApi.getCatalog(slug!),
    enabled: !!slug,
  });

  const brand = store ? getBranding(store) : null;

  const { product, categoryId, categoryName } = useMemo(() => {
    if (!catalog) return { product: null, categoryId: null as string | null, categoryName: null as string | null };
    for (const cat of catalog.categories) {
      const found = cat.products.find((p) => p.id === productId);
      if (found) return { product: found, categoryId: cat.id, categoryName: cat.name };
    }
    return { product: null, categoryId: null, categoryName: null };
  }, [catalog, productId]);

  const upsell = useMemo(() => {
    if (!catalog || !product) return [];
    return getUpsellProducts(catalog.categories, product, categoryId, catalog.categoryAffinity);
  }, [catalog, product, categoryId]);

  // SEO/compartilhamento básico -- mesmo espírito do document.title já
  // injetado em StorePage.tsx, só que pro produto específico.
  useEffect(() => {
    if (product && store) {
      document.title = `${product.name} · ${store.name}`;
    }
    return () => {
      if (store) document.title = store.name;
    };
  }, [product, store]);

  if (isLoading || !brand) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <Loader2 className="animate-spin text-gray-400" size={28} />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center gap-3 text-center px-8">
        <p className="text-3xl">🔍</p>
        <p className="text-gray-600 text-sm">Não encontramos esse produto.</p>
        <Link to={`/${slug}`} className="text-sm font-semibold" style={{ color: brand.primaryColor }}>
          Voltar pro cardápio
        </Link>
      </div>
    );
  }

  const cartItem = items.find((i) => i.product.id === product.id);
  const unitPriceAtQty = cartItem ? getUnitPriceForQuantity(product, cartItem.quantity / 100) : product.deliveryPriceCents;

  return (
    // 12/07/2026 (revisão de design): moldura max-width -- sem isso, em tela
    // larga a imagem quadrada do produto virava gigante (aspect-square numa
    // coluna de 1300px+ de largura), empurrando nome/preço/descrição pra
    // muito longe do topo. Com a moldura, o conteúdo sempre se comporta como
    // se estivesse num celular, não importa a largura da tela. Elementos
    // `fixed` (o CTA no rodapé) precisam do mesmo `max-w` + `mx-auto`
    // separadamente -- `position: fixed` ignora a moldura por padrão.
    <div className="min-h-dvh bg-gray-100">
    <div className="max-w-[480px] mx-auto min-h-dvh bg-gray-50 pb-28 shadow-xl">
      {/* Header -- 12/07/2026 (a pedido do Thales, "a sub página não é no
          mesmo formato do site"): a página de produto era uma barra branca
          genérica, sem nenhum traço da marca (logo/cor), enquanto a home
          tem banner + logo + nome em destaque. Adicionado logo pequeno +
          nome da loja aqui pra não parecer que o cliente saiu do site ao
          abrir um produto. Faixa fina com primaryColor reforça a mesma
          identidade sem repetir o banner inteiro (que não cabe/não faz
          sentido numa tela de detalhe). */}
      <div style={{ backgroundColor: brand.primaryColor }} className="h-1 w-full" />
      <div className="sticky top-0 z-30 bg-white border-b border-gray-100 flex items-center gap-3 px-4 py-3">
        <button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(`/${slug}`))}
          className="p-2 -ml-2 rounded-full active:bg-gray-100 shrink-0"
          aria-label="Voltar"
        >
          <ArrowLeft size={20} className="text-gray-700" />
        </button>
        {brand.logoUrl ? (
          <img src={brand.logoUrl} alt={store?.name ?? ''} className="w-7 h-7 rounded-full object-cover border border-gray-200 shrink-0" />
        ) : (
          <div style={{ backgroundColor: brand.primaryColor }} className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-white text-xs font-bold">
            {store?.name?.[0] ?? '?'}
          </div>
        )}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-800 truncate leading-tight">{store?.name}</p>
          <p className="text-xs text-gray-400 truncate leading-tight">{categoryName ?? 'Produto'}</p>
        </div>
      </div>

      {/* Imagem */}
      <div className="w-full aspect-square bg-white flex items-center justify-center border-b border-gray-100">
        {product.imageUrl ? (
          <img src={product.imageUrl} alt={product.name} className="w-full h-full object-contain p-6" />
        ) : (
          <svg className="w-24 h-24 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        )}
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Badges (só refletem flags reais já setadas pelo dono no PDV) */}
        {(product.isPromo || product.isBestSeller) && (
          <div className="flex gap-2 flex-wrap">
            {product.isPromo && (
              <span className="inline-flex items-center gap-1 bg-red-50 text-red-600 text-xs font-bold px-2 py-1 rounded-full">
                <Tag size={12} /> Promoção
              </span>
            )}
            {product.isBestSeller && (
              <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 text-xs font-bold px-2 py-1 rounded-full">
                <Star size={12} /> Mais vendido
              </span>
            )}
          </div>
        )}

        <h1 className="text-xl font-bold text-gray-900 leading-snug">{product.name}</h1>

        {/* Preço */}
        {product.hasCustomDeliveryPrice && product.deliveryPriceCents < product.salePriceCents ? (
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm text-gray-400 line-through">{fmt(product.salePriceCents)}</span>
            <span style={{ color: brand.accentColor }} className="text-2xl font-bold">{fmt(product.deliveryPriceCents)}</span>
            <span className="text-xs bg-green-100 text-green-700 font-semibold px-1.5 py-0.5 rounded-md">
              -{Math.round((1 - product.deliveryPriceCents / product.salePriceCents) * 100)}%
            </span>
          </div>
        ) : (
          <p style={{ color: brand.accentColor }} className="text-2xl font-bold">{fmt(product.deliveryPriceCents)}</p>
        )}

        {product.bulkPricing && product.bulkPricing.length > 0 && (
          <div className="flex flex-col gap-1 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
            <span className="text-xs font-semibold text-amber-700">🔥 Leve mais, pague menos</span>
            <span className="text-xs text-amber-700 leading-tight">
              {product.bulkPricing.map((t) => `${t.qty}${t.qty === 4 ? '+' : ''}un: ${fmt(t.unitPriceCents)}/un`).join(' · ')}
            </span>
          </div>
        )}

        {/* Descrição completa (sem line-clamp -- aqui é a página certa pra
            isso, diferente do card compacto da vitrine) */}
        {(product.deliveryDescription || product.description) && (
          <p className="text-sm text-gray-600 leading-relaxed">
            {product.deliveryDescription || product.description}
          </p>
        )}

        {/* Upsell / cross-sell */}
        {upsell.length > 0 && (
          <div className="pt-2">
            <h2 className="text-sm font-bold text-gray-900 mb-3">Combina bem com esse pedido</h2>
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1 -mx-4 px-4">
              {upsell.map((p) => (
                <div key={p.id} className="w-36 shrink-0">
                  <ProductCard product={p} brand={brand} onOpen={() => navigate(`/${slug}/produto/${p.id}`)} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CTA fixo (áreas clicáveis ≥44px, mobile-first) */}
      <div className="fixed bottom-0 left-0 right-0 mx-auto max-w-[480px] bg-white border-t border-gray-100 px-4 py-3 flex items-center gap-3">
        {!cartItem ? (
          <button
            onClick={() => addItem(product)}
            style={{ backgroundColor: brand.primaryColor }}
            className="flex-1 text-white font-semibold py-3.5 rounded-2xl text-base active:opacity-90 transition-opacity"
          >
            Adicionar · {fmt(product.deliveryPriceCents)}
          </button>
        ) : (
          <>
            <div className="flex items-center gap-3 bg-gray-50 rounded-2xl px-3 py-2.5">
              <button
                onClick={() => (cartItem.quantity === 100 ? removeItem(product.id) : updateQuantity(product.id, cartItem.quantity - 100))}
                style={{ color: brand.primaryColor }}
                className="p-1.5"
                aria-label="Diminuir quantidade"
              >
                {cartItem.quantity === 100 ? <Trash2 size={18} /> : <Minus size={18} />}
              </button>
              <span className="text-base font-bold text-gray-800 w-5 text-center">{cartItem.quantity / 100}</span>
              <button
                onClick={() => updateQuantity(product.id, cartItem.quantity + 100)}
                style={{ color: brand.primaryColor }}
                className="p-1.5"
                aria-label="Aumentar quantidade"
              >
                <Plus size={18} />
              </button>
            </div>
            <button
              onClick={() => navigate(`/${slug}`)}
              style={{ backgroundColor: brand.primaryColor }}
              className="flex-1 text-white font-semibold py-3.5 rounded-2xl text-base active:opacity-90 transition-opacity"
            >
              Ver carrinho · {fmt(unitPriceAtQty * (cartItem.quantity / 100))}
            </button>
          </>
        )}
      </div>
    </div>
    </div>
  );
}
